/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */
/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================
* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
=========================================================
* Este archivo define la estructura y los datos de la tabla de proyectos del dashboard.
* Obtiene datos de empresas, usuarios y activos desde la API y los transforma para la tabla.
*/

// @mui material components
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Images (logos y avatares de ejemplo)
import logoXD from "assets/images/small-logos/logo-xd.svg";
import team1 from "assets/images/team-1.jpg";

import axios from "axios";
import API_ENDPOINTS from "config/api";

/**
 * Obtiene y construye los datos para la tabla de proyectos.
 * @returns {Promise<{columns: Array, rows: Array}>}
 */
export default async function data(companyId) {
  /**
   * Obtiene los datos de la API y los transforma en filas para la tabla.
   * @returns {Promise<Array>}
   */
  const fetchData = async () => {
    try {
      // Llama a las APIs de empresas, usuarios y activos en paralelo
      const [company, responsable, activos] = await Promise.all([
        axios.get(API_ENDPOINTS.EMPRESAS),
        axios.get(API_ENDPOINTS.USUARIOS),
        axios.get(API_ENDPOINTS.INVENTARIO.GENERAL),
      ]);

      let companyData = company.data || [];
      const responsableData = responsable.data || [];
      const activosData = activos.data || [];

      // Filtrar por empresa si hay un companyId
      if (companyId) {
        companyData = companyData.filter((empresa) => String(empresa.id) === String(companyId));
      }

      const rows = [];

      // Construye cada fila de la tabla
      for (let i = 0; i < companyData.length; i++) {
        const empresa = companyData[i];

        // Calcula el total de activos para la empresa (usando precio_venta y stock_actual)
        const activosInv = activosData
          .filter((activo) => activo.sucursal_id === empresa.id || activo.empresa_id === empresa.id)
          .reduce(
            (total, activo) =>
              total + (Number(activo.precio_venta) || 0) * (Number(activo.stock_actual) || 0),
            0
          );

        // Formatea el total de activos con puntos como separador de miles
        const activosFormateados = activosInv.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");

        // Puedes mejorar la lógica para asignar el responsable real si tienes esa relación
        const responsableNombre = responsableData[0]?.nombre || "Juan Pérez";

        let logo = empresa.logo || logoXD;
        if (typeof logo === "object") {
          logo = logo.src || logoXD;
        }

        rows.push({
          logoCompany: logo,
          company: empresa.nombre,
          responsable: responsableNombre,
          activos: activosFormateados,
          estado: empresa.estado,
        });
      }
      return rows;
    } catch (error) {
      console.error("Error fetching data:", error);
      return [];
    }
  };

  /**
   * Renderiza avatares de responsables.
   * @param {Array} members - Array de arrays [imagen, nombre]
   * @returns {JSX.Element[]}
   */
  const avatars = (members) =>
    members.map(([image, name]) => (
      <Tooltip key={name} title={name} placement="bottom">
        <MDAvatar
          src={image}
          alt={name}
          size="xs"
          sx={{
            border: ({ borders: { borderWidth }, palette: { white } }) =>
              `${borderWidth[2]} solid ${white.main}`,
            cursor: "pointer",
            position: "relative",
            "&:not(:first-of-type)": {
              ml: -1.25,
            },
            "&:hover, &:focus": {
              zIndex: "10",
            },
          }}
        />
      </Tooltip>
    ));

  /**
   * Renderiza la celda de compañía con logo y nombre.
   * @param {Object} props
   * @param {string} props.image
   * @param {string} props.name
   * @returns {JSX.Element}
   */
  const Company = ({ image, name }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDAvatar src={image} name={name} size="sm" />
      <MDTypography variant="button" fontWeight="medium" ml={1} lineHeight={1}>
        {name}
      </MDTypography>
    </MDBox>
  );

  // Obtiene las filas de la API
  const rows = await fetchData();

  // Si no hay datos, retorna columnas vacías
  if (!rows || rows.length === 0) {
    return {
      columns: [
        {
          Header: (
            <MDTypography variant="button" fontWeight="bold">
              Compañía
            </MDTypography>
          ),
          accessor: "company",
          width: "30%",
          align: "left",
        },
        {
          Header: (
            <MDTypography variant="button" fontWeight="bold">
              Responsable
            </MDTypography>
          ),
          accessor: "responsable",
          width: "20%",
          align: "left",
        },
        {
          Header: (
            <MDTypography variant="button" fontWeight="bold">
              Activos (USD)
            </MDTypography>
          ),
          accessor: "activos",
          align: "center",
        },
        {
          Header: (
            <MDTypography variant="button" fontWeight="bold">
              Estado
            </MDTypography>
          ),
          accessor: "estado",
          align: "center",
        },
      ],
      rows: [],
    };
  }

  // Retorna columnas y filas formateadas para la tabla
  return {
    columns: [
      {
        Header: (
          <MDTypography variant="button" fontWeight="bold">
            Compañía
          </MDTypography>
        ),
        accessor: "company",
        width: "30%",
        align: "left",
      },
      {
        Header: (
          <MDTypography variant="button" fontWeight="bold">
            Responsable
          </MDTypography>
        ),
        accessor: "responsable",
        width: "20%",
        align: "left",
      },
      {
        Header: (
          <MDTypography variant="button" fontWeight="bold">
            Activos (USD)
          </MDTypography>
        ),
        accessor: "activos",
        align: "center",
      },
      {
        Header: (
          <MDTypography variant="button" fontWeight="bold">
            Estado
          </MDTypography>
        ),
        accessor: "estado",
        align: "center",
      },
    ],
    rows: rows.map((fila) => ({
      company: <Company image={fila.logoCompany} name={fila.company} />,
      responsable: avatars([[team1, fila.responsable]]),
      activos: (
        <MDTypography variant="caption" color="text" fontWeight="bold">
          {"$ " + fila.activos}
        </MDTypography>
      ),
      estado: (
        <MDTypography
          variant="caption"
          color={
            fila.estado && String(fila.estado).toLowerCase() === "activo" ? "success" : "error"
          }
          fontWeight="medium"
        >
          {fila.estado && String(fila.estado).toLowerCase() === "activo" ? "Activo" : "Inactivo"}
        </MDTypography>
      ),
    })),
  };
}
